import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
} from "services/api";

const ServiceTemplateContext = createContext();

export const ServiceTemplateProvider = ({ children }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRequest("/service-template");
      if (response.success) {
        setTemplates(response.data || []);
        setError(null);
      } else {
        throw new Error(response.message || "Failed to fetch templates");
      }
    } catch (err) {
      setError(err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Create new template
  const createTemplate = useCallback(
    async (templateData) => {
      setLoading(true);
      try {
        const response = await postRequest("/service-template", templateData);
        if (response.success) {
          await fetchTemplates(); // Refresh the list
          setError(null);
          return response.data;
        } else {
          throw new Error(response.message || "Failed to create template");
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchTemplates]
  );

  // Update template
  const updateTemplate = useCallback(
    async (id, templateData) => {
      setLoading(true);
      try {
        const response = await patchRequest(
          `/service-template/${id}`,
          templateData
        );
        if (response.success) {
          await fetchTemplates(); // Refresh the list
          setError(null);
        } else {
          throw new Error(response.message || "Failed to update template");
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchTemplates]
  );

  // Delete template
  const deleteTemplate = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await deleteRequest(`/service-template/${id}`);
        if (response.success) {
          await fetchTemplates(); // Refresh the list
          setError(null);
        } else {
          throw new Error(response.message || "Failed to delete template");
        }
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchTemplates]
  );

  // Get template by ID
  const getTemplateById = useCallback(
    (id) => {
      return templates.find((template) => template.id === id);
    },
    [templates]
  );

  const value = {
    templates,
    loading,
    error,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplateById,
  };

  return (
    <ServiceTemplateContext.Provider value={value}>
      {children}
    </ServiceTemplateContext.Provider>
  );
};

export const useServiceTemplates = () => {
  const context = useContext(ServiceTemplateContext);
  if (!context) {
    throw new Error(
      "useServiceTemplates must be used within a ServiceTemplateProvider"
    );
  }
  return context;
};
